import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  CssBaseline,
  Box,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Menu from '../components/Menu';

import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  BusinessCenter as BusinessCenterIcon,
  AddBusiness as AddBusinessIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  ViewList as ViewListIcon,
  Add as AddIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Assignment as AssignmentIcon,
  DynamicForm as DynamicFormIcon,
  MiscellaneousServices as ServicesIcon,
  Build as BuildIcon,
  Public as PublicIcon,
  Category as CategoryIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 260;

function AdminPage() {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  // Auto-cerrar en móvil
  React.useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  // Cerrar drawer en móvil al navegar
  const handleMenuClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  // Menú profesional reorganizado V2.1
  const menuOptions = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin',
      color: '#1A237E',
      onClick: handleMenuClick
    },
    {
      text: 'Gestión de Empresas',
      icon: <BusinessIcon />,
      color: '#4FC3F7',
      subOptions: [
        {
          text: 'Empresas',
          icon: <ViewListIcon />,
          path: '/admin/empresas',
          onClick: handleMenuClick,
          description: 'Gestionar negocios pendientes y empresas activas'
        },
        {
          text: 'Crear Empresa',
          icon: <AddIcon />,
          path: '/admin/empresas/crear',
          onClick: handleMenuClick
        }
      ]
    },
    {
      text: 'Usuarios del Sistema',
      icon: <GroupIcon />,
      color: '#29B6F6',
      subOptions: [
        {
          text: 'Listar Usuarios',
          icon: <PeopleIcon />,
          path: '/admin/usuarios',
          onClick: handleMenuClick
        },
        {
          text: 'Crear Usuario',
          icon: <PersonAddIcon />,
          path: '/admin/usuarios/crear',
          onClick: handleMenuClick
        }
      ]
    },
    {
      text: 'Servicios',
      icon: <ServicesIcon />,
      color: '#FFA726',
      path: '/admin/servicios',
      onClick: handleMenuClick
    },
    {
      text: 'Configuración de Campos',
      icon: <DynamicFormIcon />,
      color: '#66BB6A',
      subOptions: [
        {
          text: 'Catálogo de Campos',
          icon: <ViewListIcon />,
          path: '/admin/catalogo-campos',
          onClick: handleMenuClick,
          description: 'Crear y administrar campos dinámicos'
        },
        {
          text: 'Asignar a Empresas',
          icon: <TuneIcon />,
          path: '/admin/campos-empresas',
          onClick: handleMenuClick,
          description: 'Configurar campos por estado y empresa'
        }
      ]
    },
    {
      text: 'Suscripciones',
      icon: <AssignmentIcon />,
      color: '#F59E0B',
      path: '/admin/suscripciones',
      onClick: handleMenuClick,
      description: 'Planes, trials y pagos de empresas'
    },
    {
      text: 'Mantenimiento',
      icon: <BuildIcon />,
      color: '#78909C',
      subOptions: [
        {
          text: 'Gestión de Plantillas',
          icon: <AssignmentIcon />,
          path: '/admin/mantenimiento/plantillas',
          onClick: handleMenuClick,
          description: 'Plantillas de proceso V1'
        },
        {
          text: 'Campos Globales (V1)',
          icon: <DynamicFormIcon />,
          path: '/admin/mantenimiento/campos',
          onClick: handleMenuClick,
          description: 'Campos dinámicos globales V1'
        },
        {
          text: 'Países',
          icon: <PublicIcon />,
          path: '/admin/mantenimiento/paises',
          onClick: handleMenuClick
        },
        {
          text: 'Rubros',
          icon: <CategoryIcon />,
          path: '/admin/mantenimiento/rubros',
          onClick: handleMenuClick
        }
      ]
    }
  ];

  // Determinar título de la página actual
  const getCurrentPageTitle = () => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      return 'Dashboard';
    }

    // Buscar en opciones de menú
    for (const option of menuOptions) {
      if (option.path === location.pathname) {
        return option.text;
      }
      if (option.subOptions) {
        const subOption = option.subOptions.find(sub => sub.path === location.pathname);
        if (subOption) {
          return subOption.text;
        }
      }
    }

    return 'Panel de Administración';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* AppBar con gradiente TRACKEA */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
          boxShadow: '0 4px 20px rgba(26, 35, 126, 0.2)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          })
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              marginRight: 2,
              borderRadius: 2,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          <Box display="flex" alignItems="center" flexGrow={1}>
            <AdminIcon sx={{ mr: 1.5, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" noWrap fontWeight="bold">
                {getCurrentPageTitle()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Panel de Administración
              </Typography>
            </Box>
          </Box>

          {/* Avatar del admin */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right" sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight="bold">
                {user?.nombre || 'Admin'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Administrador
              </Typography>
            </Box>
            <Avatar
              sx={{
                bgcolor: '#4FC3F7',
                width: 45,
                height: 45,
                fontWeight: 'bold',
                fontSize: 18,
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              {user?.nombre?.charAt(0) || 'A'}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menu lateral */}
      <Menu menuOptions={menuOptions} open={open} onToggle={handleDrawerToggle} />

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#F5F5F5',
          p: 3,
          minHeight: '100vh',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          })
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminPage;
